import { ITaskInputText, TasksApi } from 'api';
import { addNoteForTextTaskMutation, completeTaskMutation, sendQuestionnaireAITextTaskMutation } from 'api/tasks/query';
import { MODALS_STORE, TASKS_MODAL_ID } from 'constants/ui/modal';
import { useLocalization } from 'localization';
import QUERIES from 'modules/common/queries';
import steps, { AITextInputFormData, SubmitFormRef } from 'modules/v2/servicesmodal/tasks/AITextInputTaskModalContent';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

export const useAITextInputTaskModal = () => {
  const { t } = useLocalization();

  const { toggleModal, isModalOpen, getModalData, setModalData } = MODALS_STORE.tasks();
  const reference = getModalData(TASKS_MODAL_ID.AITextInput)?.task?.caseReference;
  const taskStatus = getModalData(TASKS_MODAL_ID.AITextInput)?.task?.status;
  const activeStep = getModalData(TASKS_MODAL_ID.AITextInput)?.aiTextInput?.activeStep ?? 0;
  const taskId = getModalData(TASKS_MODAL_ID.AITextInput)?.task?.id;

  const [isQuestionnaireAvailable, setIsQuestionnaireAvailable] = useState(false);
  const [detailsFormData, setDetailsFormData] = useState<AITextInputFormData | null>(null);
  const detailFormRef = useRef<SubmitFormRef>(null);
  const questionnaireFormRef = useRef<SubmitFormRef>(null);
  const [isQuestionnaireSubmitting, setIsQuestionnaireSubmitting] = useState(false);

  const { data: { isSendEmailAvailable } = {}, isLoading: isQuestionnaireLoading } = useQuery({
    queryKey: [QUERIES.questionnaire, taskId],
    queryFn: () => TasksApi.getAITextQuestionnaire(taskId as string),
    enabled: !!taskId,
  });

  const { mutateAsync: mutateAsyncCreateComment, isPending: isCreateCommentLoading } = addNoteForTextTaskMutation();
  const { mutateAsync: mutateAsyncCompleteTask, isLoading: isCompleteTaskLoading } = completeTaskMutation();
  const { mutateAsync: mutateAsyncSendQuestionnaire, isPending: isSendQuestionnaireLoading } =
    sendQuestionnaireAITextTaskMutation();

  const isMutateLoading =
    isCreateCommentLoading || isCompleteTaskLoading || isSendQuestionnaireLoading || isQuestionnaireSubmitting;

  useEffect(() => {
    if (isSendEmailAvailable === false) {
      setIsQuestionnaireAvailable(true);
    }
  }, [isSendEmailAvailable]);

  const mapInputTextToNote = useCallback(
    (data: AITextInputFormData) => {
      const questionnaire = data.isQuestionnaire
        ? [
            t('common.terms.yes'),
            `${t('common.terms.name')}: ${data.contactPersonName}`,
            `${t('common.terms.email')}: ${data.email}`,
          ]
            .filter(Boolean)
            .join(', ')
        : t('common.terms.no');

      return [
        `${t('modals.aiTextDetails.inputArea.title')}: ${data.importantElement}`,
        `${t('common.terms.propertyType')}: ${data.property.type}`,
        `${t('modals.aiTextDetails.shouldQuestionnaireBeConductedText')}: ${questionnaire}`,
        `${t('common.terms.responsibleAgent')}: ${data.responsibleAgent}`,
      ].join(', ');
    },
    [t]
  );

  const onSubmit = useCallback(
    async (formData = detailsFormData) => {
      if (!formData) return;

      const inputText: ITaskInputText = {
        email: formData.email || '',
        name: formData.contactPersonName || '',
        taskId,
      };

      if (formData.isQuestionnaire) {
        await mutateAsyncSendQuestionnaire(inputText);
      }

      await mutateAsyncCreateComment({
        note: mapInputTextToNote(formData),
        taskId,
        addedByEmail: formData.email || '',
      });

      await mutateAsyncCompleteTask(taskId);

      toggleModal(TASKS_MODAL_ID.AITextInput);
    },
    [detailsFormData]
  );

  const handleDetailsFormSubmit = async (formData: AITextInputFormData) => {
    // validation success at this point
    setDetailsFormData(formData);

    // if step is skipped - proceed with requests
    if (!isQuestionnaireAvailable) {
      await onSubmit(formData);
      return;
    }

    setModalData(TASKS_MODAL_ID.AITextInput, {
      ...getModalData(TASKS_MODAL_ID.AITextInput),
      aiTextInput: {
        ...getModalData(TASKS_MODAL_ID.AITextInput).aiTextInput,
        activeStep: activeStep + 1,
      },
    });
  };

  const handleClickConfirm = async () => {
    switch (activeStep) {
      case 0:
        detailFormRef.current?.submit();
        break;
      case 1:
        if (questionnaireFormRef.current) {
          const validationPassed = await questionnaireFormRef.current.submit();
          if (!validationPassed) return;

          await onSubmit();
        }
        break;
    }
  };

  const handleClickCancel = () => {
    if (activeStep === 0) {
      toggleModal(TASKS_MODAL_ID.AITextInput);
    } else {
      setModalData(TASKS_MODAL_ID.AITextInput, {
        ...getModalData(TASKS_MODAL_ID.AITextInput),
        aiTextInput: {
          ...getModalData(TASKS_MODAL_ID.AITextInput).aiTextInput,
          activeStep: activeStep - 1,
        },
      });
    }
  };

  return {
    isQuestionnaireAvailable,
    t,
    reference,
    taskStatus,
    activeStep,
    isMutateLoading,
    isQuestionnaireLoading,
    steps,
    detailsFormData,
    detailFormRef,
    questionnaireFormRef,
    setIsQuestionnaireAvailable,
    handleDetailsFormSubmit,
    handleClickConfirm,
    handleClickCancel,
    setIsQuestionnaireSubmitting,
    isModalOpen,
    toggleModal,
  };
};
